// Public data access. Runs entirely in the browser (or during SSR) using the
// Supabase publishable key + RLS policies — no server functions required, so the
// app can be hosted as a static SPA.
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

export type Row = any;

const LISTS = {
  proposals: { order: [["sort_order", true], ["created_at", false]] },
  works: { order: [["sort_order", true], ["date", false]] },
  news: { order: [["featured", false], ["published_at", false]] },
  events: { order: [["event_date", true]] },
  videos: { order: [["sort_order", true]] },
  galleries: { order: [["sort_order", true]] },
  testimonials: { order: [["sort_order", true]] },
  timeline: { order: [["sort_order", true]] },
  numbers: { order: [["sort_order", true]] },
  materials: { order: [["sort_order", true], ["created_at", false]] },
  proposal_categories: { order: [["sort_order", true]] },
  work_categories: { order: [["sort_order", true]] },
  news_categories: { order: [["sort_order", true]] },
} as const;

type ListTable = keyof typeof LISTS;

async function readList(table: ListTable, limit?: number) {
  let q = supabase.from(table).select("*");
  for (const [col, asc] of LISTS[table].order as any) {
    q = q.order(col, { ascending: asc });
  }
  if (limit) q = q.limit(limit);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as Row[];
}

export async function getSiteSettings() {
  const { data, error } = await supabase.from("site_settings").select("key,value");
  if (error) throw new Error(error.message);
  const out: Record<string, Row> = {};
  for (const r of data ?? []) out[(r as any).key] = ((r as any).value ?? {}) as Row;
  return out;
}

const listSchema = z.object({
  table: z.enum(Object.keys(LISTS) as [ListTable, ...ListTable[]]),
  limit: z.number().int().positive().max(60).optional(),
});

export async function getList({ data }: { data: unknown }) {
  const input = listSchema.parse(data);
  return readList(input.table, input.limit);
}

const ITEMS = ["proposals", "works", "news"] as const;
const itemSchema = z.object({ table: z.enum(ITEMS), slug: z.string().min(1).max(200) });

export async function getItem({ data }: { data: unknown }) {
  const input = itemSchema.parse(data);
  const { data: row, error } = await supabase
    .from(input.table)
    .select("*")
    .eq("slug", input.slug)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (row ?? null) as Row | null;
}

export async function getGallery({ data }: { data: unknown }) {
  const input = z.object({ slug: z.string().min(1).max(200) }).parse(data);
  const { data: gallery, error } = await supabase
    .from("galleries")
    .select("*")
    .eq("slug", input.slug)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!gallery) return null;
  const { data: images, error: imgError } = await supabase
    .from("gallery_images")
    .select("*")
    .eq("gallery_id", (gallery as any).id)
    .order("sort_order", { ascending: true });
  if (imgError) throw new Error(imgError.message);
  return { gallery: gallery as Row, images: (images ?? []) as Row[] };
}

export async function getPage({ data }: { data: unknown }) {
  const input = z.object({ slug: z.string().min(1).max(80) }).parse(data);
  const { data: row, error } = await supabase
    .from("pages")
    .select("*")
    .eq("slug", input.slug)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (row ?? null) as Row | null;
}

export async function getHomeData() {
  const [numbers, proposals, works, news, events, testimonials, videos, timeline] =
    await Promise.all([
      readList("numbers"),
      readList("proposals"),
      readList("works"),
      readList("news", 3),
      readList("events"),
      readList("testimonials"),
      readList("videos"),
      readList("timeline"),
    ]);
  const today = new Date().toISOString().slice(0, 10);
  return {
    numbers,
    proposals: proposals.slice(0, 6),
    works: works.slice(0, 3),
    news,
    events: events.filter((e) => (e["event_date"] as string) >= today).slice(0, 3),
    testimonials,
    videos: videos.slice(0, 3),
    timeline,
  };
}

const contactSchema = z.object({
  name: z.string().min(2).max(120),
  email: z.string().email().max(160).optional().or(z.literal("")),
  phone: z.string().max(40).optional().or(z.literal("")),
  city: z.string().max(120).optional().or(z.literal("")),
  subject: z.string().max(160).optional().or(z.literal("")),
  message: z.string().min(5).max(4000),
  consent: z.boolean(),
});

export async function submitContact({ data }: { data: unknown }) {
  const input = contactSchema.parse(data);
  const { error } = await supabase.from("contacts").insert(input as any);
  if (error) throw new Error(error.message);
  return { ok: true };
}

const participationSchema = z.object({
  name: z.string().min(2).max(120),
  email: z.string().email().max(160).optional().or(z.literal("")),
  phone: z.string().max(40).optional().or(z.literal("")),
  city: z.string().max(120).optional().or(z.literal("")),
  neighborhood: z.string().max(120).optional().or(z.literal("")),
  interest: z.string().min(2).max(120),
  message: z.string().max(4000).optional().or(z.literal("")),
  consent: z.boolean(),
});

export async function submitParticipation({ data }: { data: unknown }) {
  const input = participationSchema.parse(data);
  const { error } = await supabase.from("participation_forms").insert(input as any);
  if (error) throw new Error(error.message);
  return { ok: true };
}
